import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Popover,
  Divider,
  TextField,
} from '@mui/material';
import { es } from 'date-fns/locale';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
} from 'date-fns';
import { chartPalette } from './chartPalette';

function toInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function fromInputValue(s: string): Date {
  const d = new Date(s);
  return isNaN(d.getTime()) ? new Date() : d;
}

export type DateRangePresetId =
  | 'last7'
  | 'last30'
  | 'last90'
  | 'last365'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisYear'
  | 'lastMonth'
  | 'custom';

export interface DateRangeValue {
  startDate: Date;
  endDate: Date;
  presetId: DateRangePresetId;
}

const PRESETS: { id: DateRangePresetId; label: string }[] = [
  { id: 'last7', label: 'Últimos 7 días' },
  { id: 'last30', label: 'Últimos 30 días' },
  { id: 'last90', label: 'Últimos 90 días' },
  { id: 'last365', label: 'Últimos 365 días' },
  { id: 'thisWeek', label: 'Esta semana' },
  { id: 'thisMonth', label: 'Este mes' },
  { id: 'thisYear', label: 'Este año' },
  { id: 'lastMonth', label: 'El mes pasado' },
  { id: 'custom', label: 'Personalizado' },
];

export function getRangeFromPreset(presetId: DateRangePresetId): {
  startDate: Date;
  endDate: Date;
} {
  const today = new Date();
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (presetId) {
    case 'last7':
      return { startDate: subDays(now, 6), endDate: now };
    case 'last30':
      return { startDate: subDays(now, 29), endDate: now };
    case 'last90':
      return { startDate: subDays(now, 89), endDate: now };
    case 'last365':
      return { startDate: subDays(now, 364), endDate: now };
    case 'thisWeek':
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case 'thisMonth':
      return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
    case 'thisYear':
      return { startDate: startOfYear(now), endDate: endOfYear(now) };
    case 'lastMonth':
      const lastMonth = subMonths(now, 1);
      return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
    case 'custom':
      return { startDate: subDays(now, 6), endDate: now };
    default:
      return { startDate: subDays(now, 6), endDate: now };
  }
}

function getPresetLabel(presetId: DateRangePresetId, startDate: Date, endDate: Date): string {
  if (presetId === 'custom') {
    return `Personalizado: ${format(startDate, 'd MMM yyyy', { locale: es })} - ${format(endDate, 'd MMM yyyy', { locale: es })}`;
  }
  const preset = PRESETS.find((p) => p.id === presetId);
  return `${preset?.label ?? presetId}: ${format(startDate, 'd MMM yyyy', { locale: es })} - ${format(endDate, 'd MMM yyyy', { locale: es })}`;
}

export interface DashboardDateRangePickerProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  open?: boolean;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
  inline?: boolean;
  trigger?: React.ReactNode;
  mode?: 'light' | 'dark';
}

const isDark = (mode: 'light' | 'dark'): boolean => mode === 'dark';

export const DashboardDateRangePicker: React.FC<DashboardDateRangePickerProps> = ({
  value,
  onChange,
  open = false,
  anchorEl = null,
  onClose,
  inline = false,
  trigger,
  mode = 'dark',
}) => {
  const [presetId, setPresetId] = useState<DateRangePresetId>(value.presetId);
  const [customStart, setCustomStart] = useState<Date>(value.startDate);
  const [customEnd, setCustomEnd] = useState<Date>(value.endDate);
  const [pendingRange, setPendingRange] = useState<DateRangeValue>(value);

  useEffect(() => {
    if (open) {
      setPresetId(value.presetId);
      setPendingRange(value);
      setCustomStart(value.startDate);
      setCustomEnd(value.endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value.presetId, value.startDate, value.endDate]);

  const effectiveStart = presetId === 'custom' ? customStart : pendingRange.startDate;
  const effectiveEnd = presetId === 'custom' ? customEnd : pendingRange.endDate;

  const displayLabel = useMemo(
    () => getPresetLabel(presetId, effectiveStart, effectiveEnd),
    [presetId, effectiveStart, effectiveEnd],
  );

  const handlePresetChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newPresetId = event.target.value as DateRangePresetId;
    setPresetId(newPresetId);
    if (newPresetId !== 'custom') {
      const { startDate, endDate } = getRangeFromPreset(newPresetId);
      setPendingRange({ startDate, endDate, presetId: newPresetId });
      setCustomStart(startDate);
      setCustomEnd(endDate);
    }
  }, []);

  const handleStartInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const date = fromInputValue(e.target.value);
    setCustomStart(date);
    setCustomEnd((prev) => (prev < date ? date : prev));
  }, []);

  const handleEndInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const date = fromInputValue(e.target.value);
    setCustomEnd(date);
    setCustomStart((prev) => (prev > date ? date : prev));
  }, []);

  const handleActualizar = useCallback(() => {
    const start = presetId === 'custom' ? customStart : pendingRange.startDate;
    const end = presetId === 'custom' ? customEnd : pendingRange.endDate;
    const normalized =
      start > end ? { startDate: end, endDate: start } : { startDate: start, endDate: end };
    onChange({
      ...normalized,
      presetId: presetId === 'custom' ? 'custom' : presetId,
    });
    onClose?.();
  }, [presetId, customStart, customEnd, pendingRange, onChange, onClose]);

  const handleCancelar = useCallback(() => {
    setPresetId(value.presetId);
    setPendingRange(value);
    setCustomStart(value.startDate);
    setCustomEnd(value.endDate);
    onClose?.();
  }, [value, onClose]);

  const dark = isDark(mode);

  const content = (
    <Paper
      elevation={8}
      sx={{
        p: 2,
        minWidth: 460,
        maxWidth: 640,
        borderRadius: 2,
        ...(dark
          ? {
              background:
                'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(10px)',
            }
          : {
              background: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }),
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: dark ? '#ffffff' : '#0f172a',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {displayLabel}
      </Typography>
      <Divider
        sx={{ mb: 2, borderColor: dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 200 }}>
          <RadioGroup value={presetId} onChange={handlePresetChange}>
            {PRESETS.map((preset) => (
              <FormControlLabel
                key={preset.id}
                value={preset.id}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      '&.Mui-checked': { color: chartPalette.accent.teal },
                    }}
                  />
                }
                label={preset.label}
                sx={{
                  display: 'block',
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': {
                    color: dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                    fontSize: '0.875rem',
                  },
                }}
              />
            ))}
          </RadioGroup>
        </Box>
        {presetId === 'custom' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
            <TextField
              label="Desde"
              type="date"
              value={toInputValue(effectiveStart)}
              onChange={handleStartInputChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toInputValue(effectiveEnd) }}
              fullWidth
              sx={
                dark
                  ? {
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                        '&.Mui-focused fieldset': { borderColor: '#0CBB9A99' },
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        borderRadius: 1,
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0CBB9A' },
                    }
                  : {
                      '& .MuiOutlinedInput-root': {
                        color: '#0f172a',
                        '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
                        '&:hover fieldset': { borderColor: '#0CBB9A' },
                        '&.Mui-focused fieldset': { borderColor: '#0BB296' },
                        borderRadius: 1,
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0CBB9A' },
                    }
              }
            />
            <TextField
              label="Hasta"
              type="date"
              value={toInputValue(effectiveEnd)}
              onChange={handleEndInputChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: toInputValue(effectiveStart) }}
              fullWidth
              sx={
                dark
                  ? {
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                        '&.Mui-focused fieldset': { borderColor: '#0CBB9A99' },
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                        borderRadius: 1,
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0CBB9A' },
                    }
                  : {
                      '& .MuiOutlinedInput-root': {
                        color: '#0f172a',
                        '& fieldset': { borderColor: 'rgba(12, 187, 154, 0.45)' },
                        '&:hover fieldset': { borderColor: '#0CBB9A' },
                        '&.Mui-focused fieldset': { borderColor: '#0BB296' },
                        borderRadius: 1,
                      },
                      '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0CBB9A' },
                    }
              }
            />
          </Box>
        )}
      </Box>
      <Divider
        sx={{ my: 2, borderColor: dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={handleCancelar}
          sx={
            dark
              ? {
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.45)',
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  },
                }
              : {
                  color: 'rgba(0, 0, 0, 0.7)',
                  borderColor: 'rgba(0, 0, 0, 0.25)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.45)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }
          }
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleActualizar}
          sx={{
            background: `linear-gradient(to right, ${chartPalette.accent.cyan}, ${'#0CBB9A'})`,
            color: '#fff',
            '&:hover': {
              background: `linear-gradient(to right, ${chartPalette.accent.tealDark}, #059669)`,
            },
          }}
        >
          Actualizar
        </Button>
      </Box>
    </Paper>
  );

  if (inline) {
    return <>{content}</>;
  }

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancelar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              ...(dark
                ? {
                    background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  }
                : {
                    background: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  }),
            },
          },
        }}
      >
        {content}
      </Popover>
    </>
  );
};
